import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import { describePublishResult, describePublishConflicts } from '@/utils/automationBuilder';

/**
 * PublishResultSheet — honest §15.4 publish outcome for v2 definitions.
 *
 * Two modes:
 *  - result:   successful automation_publish response → revision, impact
 *              counts, reconcile lines, conflicts list, validation warnings.
 *  - errors:   validate_bindings / publish-rejection errors → per-step
 *              binding problems (e.g. after a trigger/source change).
 */
const PublishResultSheet = ({
    isOpen, onClose,
    result,
    errors = [],
    warnings = [],
    title,
    onReload,
    onReviewStep,
    steps = [],
}) => {
    const lines = result ? describePublishResult(result) : [];
    const conflicts = result ? describePublishConflicts(result) : [];
    const allWarnings = [...(result?.warnings || []), ...(warnings || [])];
    const stepName = (id) => steps.find(s => s.id === id)?.name || null;

    return (
        <SheetModal
            isOpen={isOpen}
            onCancel={onClose}
            onClose={onClose}
            title={title || (errors.length ? 'Automation needs attention' : 'Published')}
            cancelText="Close"
            scrollable
        >
            <ScrollView>
                {!!result && (
                    <View style={styles.block}>
                        {lines.map((line, i) => (
                            <View key={i} style={styles.lineRow}>
                                <Unicon name="checkmark-circle-outline" size={16} colorVariant="success" />
                                <Text style={styles.lineText}>{line}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {conflicts.length > 0 && (
                    <View style={styles.block}>
                        <Text style={styles.blockTitle}>Needs review</Text>
                        {conflicts.map((c, i) => (
                            <View key={i} style={styles.lineRow} testID={`publish-conflict-${i}`}>
                                <Unicon name="warning-outline" size={16} colorVariant="warning" />
                                <Text style={styles.lineText}>{c.label}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {errors.length > 0 && (
                    <View style={styles.block}>
                        <Text style={styles.blockTitle}>Incompatible bindings</Text>
                        <Text style={styles.hintText}>
                            These step settings reference variables that no longer exist. Fix each step, then publish again.
                        </Text>
                        {errors.map((e, i) => (
                            <View key={i} style={styles.errorRow} testID={`binding-error-${i}`}>
                                <View style={styles.errorRowText}>
                                    {!!stepName(e.stepID) && <Text style={styles.errorStep}>{stepName(e.stepID)}</Text>}
                                    <Text style={styles.errorText}>{e.message || e.code}</Text>
                                    {!!e.binding && <Text style={styles.errorBinding}>{e.binding}</Text>}
                                </View>
                                {!!e.stepID && !!onReviewStep && (
                                    <SolidButton
                                        variant="muted"
                                        type="primary"
                                        text="Fix"
                                        onPress={() => onReviewStep(e.stepID)}
                                    />
                                )}
                            </View>
                        ))}
                    </View>
                )}

                {allWarnings.length > 0 && (
                    <View style={styles.block}>
                        <Text style={styles.blockTitle}>Warnings</Text>
                        {allWarnings.map((w, i) => (
                            <View key={i} style={styles.lineRow}>
                                <Unicon name="alert-circle-outline" size={16} colorVariant="warning" />
                                <Text style={styles.lineText}>{w.message || w.code || String(w)}</Text>
                            </View>
                        ))}
                    </View>
                )}

                {!!onReload && (
                    <SolidButton
                        type="primary"
                        text="Reload latest"
                        onPress={onReload}
                        width="100%"
                    />
                )}
            </ScrollView>
        </SheetModal>
    );
};

const styles = StyleSheet.create((theme) => ({
    block: { marginBottom: 16 },
    blockTitle: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
    },
    lineRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 8,
        marginBottom: 6,
    },
    lineText: {
        flex: 1,
        fontSize: 13,
        color: theme.colors.text,
        lineHeight: 18,
    },
    hintText: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        lineHeight: 17,
        marginBottom: 10,
    },
    errorRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 8,
        padding: 10,
        marginBottom: 6,
    },
    errorRowText: { flex: 1 },
    errorStep: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
        marginBottom: 2,
    },
    errorText: {
        fontSize: 13,
        color: theme.colors.text,
        lineHeight: 17,
    },
    errorBinding: {
        fontSize: 11,
        color: theme.colors.textTertiary,
        marginTop: 2,
    },
}));

export default PublishResultSheet;
